import React from "react";
import { useAuth } from "../context/AuthContext";
import { AdminPermission } from "../api/generated/models/AdminPermission";
import KpiHighlights from "../components/AdminDashboard/KpiHighlights";
import ActivityHighlights from "../components/AdminDashboard/ActivityHighlights";
import QuickLinks from "../components/AdminDashboard/QuickLinks";
import { WidgetBoundary } from "../components/AdminDashboard/WidgetBoundary";
import { useDashboardData } from "../components/AdminDashboard/useDashboardData";
import { Loader } from "../components/Loader";

// The tile grid this page used to be duplicated the sidebar and had already
// drifted from it. Navigation now lives in the sidebar; the landing page leads
// with the platform's headline figures instead, gated per block so an admin
// never sees, or requests, figures they have no permission for.
//
// Everything is fetched once and rendered together. Both sources are cached
// server-side, so the page has nothing to gain from showing one block early —
// and doing so re-ranked the stat grid the moment the second arrived.
const AdminHome: React.FC = () => {
  const { hasPermission } = useAuth();

  const showKpi = hasPermission(AdminPermission.KPIREPORT);
  const showActivity = hasPermission(AdminPermission.ANALYTICS);

  const { kpi, activity, loading } = useDashboardData({
    kpi: showKpi,
    activity: showActivity,
  });

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-black/60">Dashboard</h2>
      </div>

      {(showKpi || showActivity) && loading && (
        <div className="flex justify-center py-16">
          <Loader className="w-8 h-8" />
        </div>
      )}

      {!loading && showKpi && (
        <WidgetBoundary name="Platform figures">
          {kpi ? (
            <KpiHighlights data={kpi} activity={activity} />
          ) : (
            <div className="bg-white rounded-2xl shadow p-5 mb-8 text-sm text-gray-500">
              Key figures are unavailable right now.
            </div>
          )}
        </WidgetBoundary>
      )}

      {!loading && showActivity && (
        <WidgetBoundary name="App activity">
          <ActivityHighlights activity={activity} />
        </WidgetBoundary>
      )}

      {!showKpi && !showActivity && (
        <p className="text-sm text-gray-500 mb-8">
          Your account does not have access to the platform figures. Use the
          navigation to open the sections available to you.
        </p>
      )}

      <QuickLinks />
    </div>
  );
};

export default AdminHome;
